import { createMemo, createResource, createSignal, For, Show } from "solid-js";
import { useNavigate, useSearchParams } from "@solidjs/router";
import type { AlertRule, PlatformEvent } from "@energyplatform/client";
import { api } from "../services.ts";
import { locale, t } from "../i18n.ts";
import { formatDateTime } from "../format.ts";
import { pathTo, scope, siteTree } from "../context.ts";
import { subtreeOf } from "../tree.ts";
import {
  conditionText,
  eventTitle,
  PRIORITY_CHIP,
  ruleOf,
  STAGE_CHIP,
  subjectText,
  userName,
} from "../events.ts";
import { Icon } from "../icons.tsx";
import { ErrorBox } from "../components/ErrorBox.tsx";
import { AssignDialog, CloseDialog } from "../components/EventDialogs.tsx";
import { RuleWizard } from "../components/RuleWizard.tsx";
import {
  ConfirmDialog,
  operator,
  PageHeader,
  Skeleton,
  useAction,
} from "../components/ui.tsx";

const FILTERS = ["active", "open", "unacknowledged", "mine", "all"] as const;
type Filter = (typeof FILTERS)[number];
type Tab = "list" | "rules" | "quiet";

/**
 * Events (prototype "Zdarzenia"): condition, acknowledgement and task are three separate pieces of information.
 * The list works on many events at once; an event opens its own page. Alert rules and quiet windows are tabs.
 */
export function EventsPage() {
  const [params, setParams] = useSearchParams<{ tab?: string }>();
  const tab = (): Tab =>
    params.tab === "rules" || params.tab === "quiet" ? params.tab : "list";
  const setTab = (next: Tab) =>
    setParams({ tab: next === "list" ? undefined : next });
  const [rules, { refetch: refetchRules }] = createResource(() =>
    api.events.rules(),
  );
  const [wizard, setWizard] = createSignal<AlertRule | "new">();
  return (
    <>
      <PageHeader
        eyebrow={t("events.eyebrow", { scope: scope()?.name ?? "" })}
        title={tab() === "rules" ? t("events.rules.title") : t("events.pageTitle")}
        sub={tab() === "rules" ? t("events.rules.intro") : t("events.intro")}
      >
        <Show when={operator()}>
          <button
            type="button"
            class={`btn ${tab() === "rules" ? "primary" : ""}`}
            onClick={() => setWizard("new")}
          >
            <Icon name="plus" />
            {t("events.newRule")}
          </button>
        </Show>
      </PageHeader>
      <div
        class="tabs"
        role="tablist"
        style={{ padding: 0, "margin-top": "-6px" }}
      >
        <button
          type="button"
          role="tab"
          aria-selected={tab() === "list"}
          onClick={() => setTab("list")}
        >
          {t("events.tab.events")}
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={tab() === "rules"}
          onClick={() => setTab("rules")}
        >
          {t("events.tab.rules", { count: (rules()?.length ?? 0) + 2 })}
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={tab() === "quiet"}
          onClick={() => setTab("quiet")}
          style={{ opacity: 0.6 }}
        >
          {t("events.tab.quiet")}
        </button>
      </div>
      <Show when={tab() === "list"}>
        <EventList rules={rules()} />
      </Show>
      <Show when={tab() === "rules"}>
        <Rules
          rules={rules() ?? []}
          onEdit={setWizard}
          onChanged={() => void refetchRules()}
        />
      </Show>
      <Show when={tab() === "quiet"}>
        <section class="card empty" style={{ opacity: 0.75 }}>
          <span class="chip">{t("events.wizard.soon")}</span>
          <h2>{t("events.quiet.title")}</h2>
          <p class="muted">{t("events.quiet.text")}</p>
          <div>
            <button type="button" class="btn" disabled>
              <Icon name="plus" />
              {t("events.quiet.add")}
            </button>
          </div>
        </section>
      </Show>
      <Show when={wizard()}>
        {(w) => (
          <RuleWizard
            rule={w() === "new" ? undefined : (w() as AlertRule)}
            onClose={() => setWizard(undefined)}
            onSaved={() => {
              api.invalidate("/api/v1/alert-rules");
              void refetchRules();
              setTab("rules");
            }}
          />
        )}
      </Show>
    </>
  );
}

function EventList(props: { rules: AlertRule[] | undefined }) {
  const navigate = useNavigate();
  const [filter, setFilter] = createSignal<Filter>("active");
  const [events, { refetch }] = createResource(() => api.events.list());
  const [me] = createResource(() => api.me());
  const [users] = createResource(() => api.events.users());
  const [selected, setSelected] = createSignal<ReadonlySet<string>>(new Set());
  const [dialog, setDialog] = createSignal<"assign" | "close">();
  const [run, error, busy] = useAction();

  const inScope = createMemo(() => {
    const id = scope()?.id;
    return id ? new Set(subtreeOf(siteTree()?.nodes ?? [], id)) : null;
  });
  // Integrations belong to the organisation, so their events show in every scope.
  const scoped = () =>
    (events() ?? []).filter(
      (e) =>
        e.nodeId === null || inScope() === null || inScope()!.has(e.nodeId),
    );
  const rows = createMemo(() =>
    scoped().filter((e) => {
      switch (filter()) {
        case "active":
          return e.endedAt === null;
        case "open":
          return e.stage !== "closed";
        case "unacknowledged":
          return e.acknowledgedAt === null;
        case "mine":
          return e.assignee !== null && e.assignee === me()?.name;
        case "all":
          return true;
      }
    }),
  );
  const chosen = () => rows().filter((e) => selected().has(e.id));
  const toggle = (id: string) => {
    const next = new Set(selected());
    if (next.has(id)) {
      next.delete(id);
    } else {
      next.add(id);
    }
    setSelected(next);
  };
  const reload = async () => {
    api.invalidate("/api/v1/events");
    setSelected(new Set<string>());
    await refetch();
  };
  const acknowledge = () =>
    void run(async () => {
      await api.events.acknowledge(chosen().map((e) => e.id));
      await reload();
    });
  const place = (e: PlatformEvent) =>
    e.subjectType === "connection"
      ? t("events.subject.connection")
      : pathTo(siteTree()?.nodes ?? [], e.nodeId ?? undefined)
          .map((n) => n.name)
          .join(" / ");

  return (
    <section class="card">
      <div class="filters" role="group" aria-label={t("events.filter")}>
        <For each={FILTERS}>
          {(f) => (
            <button
              type="button"
              class="fbtn"
              aria-pressed={filter() === f}
              onClick={() => setFilter(f)}
            >
              {t(`events.f.${f}`)}
            </button>
          )}
        </For>
      </div>
      <Show when={chosen().length > 0}>
        <div class="note" style={{ "flex-wrap": "wrap" }}>
          <Icon name="check" />
          <b style={{ "font-weight": 500, color: "var(--ink)" }}>
            {t("events.selected", { count: chosen().length })}
          </b>
          <button
            type="button"
            class="btn sm"
            disabled={busy()}
            onClick={acknowledge}
          >
            {t("events.acknowledge")}
          </button>
          <button
            type="button"
            class="btn sm"
            onClick={() => setDialog("assign")}
          >
            {t("events.assignMany")}
          </button>
          <button
            type="button"
            class="btn sm"
            onClick={() => setDialog("close")}
          >
            {t("events.closeMany")}
          </button>
          <button
            type="button"
            class="btn ghost sm"
            onClick={() => setSelected(new Set<string>())}
          >
            {t("events.unselect")}
          </button>
        </div>
      </Show>
      <Show when={error()}>
        <ErrorBox error={error()} />
      </Show>
      <Show when={!events.error} fallback={<ErrorBox error={events.error} />}>
        <Show
          when={events()}
          fallback={
            <div class="card-b">
              <Skeleton lines={5} />
            </div>
          }
        >
          <div class="scrollx">
            <table class="tbl">
              <thead>
                <tr>
                  <th style={{ width: "28px" }}>
                    <input
                      type="checkbox"
                      aria-label={t("events.selectAll")}
                      checked={
                        rows().length > 0 &&
                        rows().every((e) => selected().has(e.id))
                      }
                      onChange={(e) =>
                        setSelected(
                          new Set(
                            e.currentTarget.checked
                              ? rows().map((x) => x.id)
                              : [],
                          ),
                        )
                      }
                    />
                  </th>
                  <th>{t("events.th.event")}</th>
                  <th>{t("events.th.subject")}</th>
                  <th>{t("events.th.since")}</th>
                  <th>{t("events.th.condition")}</th>
                  <th>{t("events.th.ack")}</th>
                  <th>{t("events.th.task")}</th>
                  <th>{t("events.th.who")}</th>
                </tr>
              </thead>
              <tbody>
                <For
                  each={rows()}
                  fallback={
                    <tr>
                      <td colspan="8" class="hint">
                        {t("events.empty")}
                      </td>
                    </tr>
                  }
                >
                  {(e) => {
                    const rule = () => ruleOf(e, props.rules);
                    return (
                      <tr
                        class={`ev-row p${e.priority} click ${selected().has(e.id) ? "sel" : ""}`}
                        onClick={() =>
                          navigate(`/events/${encodeURIComponent(e.id)}`)
                        }
                      >
                        <td onClick={(x) => x.stopPropagation()}>
                          <input
                            type="checkbox"
                            checked={selected().has(e.id)}
                            aria-label={eventTitle(e, rule())}
                            onChange={() => toggle(e.id)}
                          />
                        </td>
                        <td>
                          <b style={{ "font-weight": 500 }}>
                            {eventTitle(e, rule())}
                          </b>
                          <Show when={e.test}>
                            {" "}
                            <span class="chip acc">{t("events.test")}</span>
                          </Show>
                          <div class="hint">
                            {t(`events.kind.${e.kind}`)} ·{" "}
                            {t("events.priority", {
                              p: t(`events.prio.${e.priority}`),
                            })}
                            {e.occurrences > 1
                              ? ` · ${t("events.occurrences", { count: e.occurrences })}`
                              : ""}
                          </div>
                        </td>
                        <td>
                          {e.subjectName}
                          <div class="hint">{place(e)}</div>
                        </td>
                        <td class="num">
                          {formatDateTime(e.startedAt, locale())}
                        </td>
                        <td>
                          <span
                            class={`chip ${e.endedAt === null ? "crit" : "ok"}`}
                          >
                            {e.endedAt === null
                              ? t("events.cond.on")
                              : t("events.cond.off")}
                          </span>
                        </td>
                        <td>
                          <span
                            class={`chip ${e.acknowledgedAt ? "" : "warn"}`}
                          >
                            {e.acknowledgedAt
                              ? t("events.ack.yes")
                              : t("events.ack.no")}
                          </span>
                        </td>
                        <td>
                          <span class={`chip ${STAGE_CHIP[e.stage] ?? ""}`}>
                            {t(`events.stage.${e.stage}`)}
                          </span>
                        </td>
                        <td>{userName(users(), e.assignee)}</td>
                      </tr>
                    );
                  }}
                </For>
              </tbody>
            </table>
          </div>
        </Show>
      </Show>
      <Show when={dialog() === "assign"}>
        <AssignDialog
          events={chosen()}
          onClose={() => setDialog(undefined)}
          onDone={reload}
        />
      </Show>
      <Show when={dialog() === "close"}>
        <CloseDialog
          events={chosen()}
          onClose={() => setDialog(undefined)}
          onDone={reload}
        />
      </Show>
    </section>
  );
}

/** Alert rules as the prototype lists them: condition, priority, recipients, escalation, how often they fired. */
function Rules(props: {
  rules: AlertRule[];
  onEdit: (rule: AlertRule) => void;
  onChanged: () => void;
}) {
  const [events] = createResource(() => api.events.list());
  const [users] = createResource(() => api.events.users());
  const [removing, setRemoving] = createSignal<AlertRule>();
  const [run, error] = useAction();
  const pointName = (id: string) =>
    siteTree()?.points.find((p) => p.id === id)?.name ?? id;
  const nodeName = (id: string) =>
    siteTree()?.nodes.find((n) => n.id === id)?.name ?? id;
  const month = Date.now() - 30 * 86_400_000;
  const fired = (key: string) =>
    (events() ?? []).filter(
      (e) => e.ruleKey === key && new Date(e.startedAt).getTime() >= month,
    );
  const last = (key: string) => fired(key)[0]?.startedAt;
  const state = (r: AlertRule) =>
    !r.enabled
      ? ["stale", t("events.rules.off")]
      : r.testUntil && new Date(r.testUntil).getTime() > Date.now()
        ? [
            "acc",
            t("events.rules.testUntil", {
              at: formatDateTime(r.testUntil, locale()),
            }),
          ]
        : r.mutedUntil && new Date(r.mutedUntil).getTime() > Date.now()
          ? [
              "warn",
              t("events.rules.mutedUntil", {
                at: formatDateTime(r.mutedUntil, locale()),
              }),
            ]
          : ["ok", t("events.rules.on")];
  const toggle = (r: AlertRule) =>
    void run(async () => {
      const {
        id,
        testUntil,
        mutedUntil: _m,
        createdAt: _c,
        createdBy: _b,
        ...input
      } = r;
      await api.events.updateRule(id, {
        ...input,
        enabled: !r.enabled,
        test: testUntil !== null && new Date(testUntil).getTime() > Date.now(),
      });
      api.invalidate("/api/v1/alert-rules");
      props.onChanged();
    });
  const builtIn = (
    key: string,
    name: string,
    subject: string,
    condition: string,
  ) => (
    <tr>
      <td>
        <span
          style={{
            display: "inline-flex",
            gap: "6px",
            "align-items": "center",
          }}
        >
          <Icon name={key === "sys:noData" ? "clock" : "plug"} />
          <b style={{ "font-weight": 500 }}>{name}</b>
        </span>
        <div class="hint">{t("events.rules.builtIn")}</div>
      </td>
      <td>
        {condition}
        <div class="hint">{subject}</div>
      </td>
      <td>
        <span class="chip warn">{t("events.rules.builtInPriority")}</span>
      </td>
      <td class="hint">{t("events.rules.builtInRecipients")}</td>
      <td class="hint">—</td>
      <td class="r num">
        {fired(key).length}
        <div class="hint">
          {last(key) ? formatDateTime(last(key), locale()) : "—"}
        </div>
      </td>
      <td>
        <span class="chip ok">{t("events.rules.on")}</span>
      </td>
    </tr>
  );
  return (
    <>
      <section class="card">
        <Show when={error()}>
          <ErrorBox error={error()} />
        </Show>
        <div class="scrollx">
          <table class="tbl">
            <thead>
              <tr>
                <th>{t("events.rules.rule")}</th>
                <th>{t("events.rules.condition")}</th>
                <th>{t("events.rules.priority")}</th>
                <th>{t("events.rules.recipients")}</th>
                <th>{t("events.rules.escalation")}</th>
                <th class="r">{t("events.rules.last30")}</th>
                <th>{t("events.rules.state")}</th>
              </tr>
            </thead>
            <tbody>
              {builtIn(
                "sys:noData",
                t("events.rules.builtInNoData"),
                t("events.rules.allRunning"),
                t("events.rule.noDataText", { minutes: 15 }),
              )}
              {builtIn(
                "sys:integration",
                t("events.rules.builtInIntegration"),
                t("events.rules.allIntegrations"),
                t("events.rules.integrationText"),
              )}
              <For each={props.rules}>
                {(r) => (
                  <tr
                    class={operator() ? "click" : ""}
                    onClick={() => operator() && props.onEdit(r)}
                  >
                    <td>
                      <span
                        style={{
                          display: "inline-flex",
                          gap: "6px",
                          "align-items": "center",
                        }}
                      >
                        <Icon name={r.kind === "noData" ? "clock" : "gauge"} />
                        <b style={{ "font-weight": 500 }}>{r.name}</b>
                      </span>
                      <div class="hint">
                        {t(
                          `events.wizard.type.${r.kind === "noData" ? "nodata" : "thr"}`,
                        )}{" "}
                        · {subjectText(r, pointName, nodeName)}
                      </div>
                    </td>
                    <td>{conditionText(r)}</td>
                    <td>
                      <span class={`chip ${PRIORITY_CHIP[r.priority] ?? ""}`}>
                        {t(`events.prio.${r.priority}`)}
                      </span>
                    </td>
                    <td>
                      {r.recipients.length > 0
                        ? r.recipients
                            .map((id) => userName(users(), id))
                            .join(", ")
                        : "—"}
                      <div class="hint">{t("events.channel.app")}</div>
                    </td>
                    <td class="hint">
                      {r.escalateAfter
                        ? t("events.wizard.escalationText", {
                            minutes: r.escalateAfter,
                            who: userName(users(), r.escalateTo),
                          })
                        : "—"}
                    </td>
                    <td class="r num">
                      {fired(`rule:${r.id}`).length}
                      <div class="hint">
                        {last(`rule:${r.id}`)
                          ? formatDateTime(last(`rule:${r.id}`), locale())
                          : "—"}
                      </div>
                    </td>
                    <td onClick={(e) => e.stopPropagation()}>
                      <span class={`chip ${state(r)[0]}`}>{state(r)[1]}</span>
                      <Show when={operator()}>
                        {" "}
                        <button
                          type="button"
                          class="btn ghost sm"
                          onClick={() => toggle(r)}
                        >
                          {r.enabled
                            ? t("events.rules.disable")
                            : t("events.rules.enable")}
                        </button>
                        <button
                          type="button"
                          class="btn ghost sm"
                          aria-label={t("common.delete")}
                          onClick={() => setRemoving(r)}
                        >
                          <Icon name="x" />
                        </button>
                      </Show>
                    </td>
                  </tr>
                )}
              </For>
            </tbody>
          </table>
        </div>
        <div class="card-b hint">{t("events.rules.hint")}</div>
      </section>
      <section class="card" style={{ opacity: 0.6 }}>
        <div class="card-h">
          <h2>{t("events.channels.title")}</h2>
          <span class="chip">{t("events.wizard.soon")}</span>
        </div>
        <div class="scrollx">
          <table class="tbl">
            <thead>
              <tr>
                <th>{t("events.rules.priority")}</th>
                <th>{t("events.channels.channels")}</th>
                <th>{t("events.channels.minimum")}</th>
              </tr>
            </thead>
            <tbody>
              <For each={[1, 2, 3]}>
                {(p) => (
                  <tr>
                    <td>
                      <span class={`chip ${PRIORITY_CHIP[p] ?? ""}`}>
                        {t(`events.prio.${p}`)}
                      </span>
                    </td>
                    <td>{t(`events.channels.p${p}`)}</td>
                    <td>{t(`events.channels.min${p}`)}</td>
                  </tr>
                )}
              </For>
            </tbody>
          </table>
        </div>
        <div class="card-b hint">{t("events.channels.hint")}</div>
      </section>
      <Show when={removing()}>
        {(r) => (
          <ConfirmDialog
            title={t("events.rules.removeTitle", { name: r().name })}
            text={t("events.rules.removeText")}
            confirm={t("common.delete")}
            danger
            onConfirm={async () => {
              await api.events.removeRule(r().id);
              api.invalidate("/api/v1/alert-rules");
              props.onChanged();
            }}
            onClose={() => setRemoving(undefined)}
          />
        )}
      </Show>
    </>
  );
}
